import type { AntigravityQuotaData, ModelQuotaInfo, QuotaResult } from '../types.ts';
import { getAntigravityAccounts } from '../auth.ts';

const GOOGLE_CLIENT_ID =
  '1071006060591-tmhssin2h21lcre235vtolojh4g403ep.apps.googleusercontent.com';
const GOOGLE_CLIENT_SECRET = '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';
const DEFAULT_PROJECT_ID = 'rising-fact-p41fc';

const ENDPOINTS: readonly string[] = [
  'https://daily-cloudcode-pa.sandbox.googleapis.com',
  'https://autopush-cloudcode-pa.sandbox.googleapis.com',
  'https://cloudcode-pa.googleapis.com',
];

const HEADERS = {
  'User-Agent': 'antigravity/1.11.5 windows/amd64',
  'X-Goog-Api-Client': 'google-cloud-sdk vscode_cloudshelleditor/0.1',
  'Client-Metadata':
    '{"ideType":"IDE_UNSPECIFIED","platform":"PLATFORM_UNSPECIFIED","pluginType":"GEMINI"}',
} as const;

interface TokenResponse {
  access_token: string;
  expires_in?: number;
}

interface ModelQuotaInfoResponse {
  displayName?: string;
  quotaInfo?: {
    remainingFraction?: number;
    resetTime?: string;
  };
}

interface ModelsResponse {
  models?: Record<string, ModelQuotaInfoResponse>;
}

const refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
  try {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID,
        client_secret: GOOGLE_CLIENT_SECRET,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as TokenResponse;
    return data.access_token;
  } catch {
    return null;
  }
};

const fetchModels = async (
  accessToken: string,
  projectId?: string
): Promise<ModelsResponse | null> => {
  const body = projectId ? { project: projectId } : {};

  for (const endpoint of ENDPOINTS) {
    try {
      const response = await fetch(`${endpoint}/v1internal:fetchAvailableModels`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...HEADERS,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(15000),
      });

      if (response.ok) {
        return (await response.json()) as ModelsResponse;
      }
    } catch {
      continue;
    }
  }

  return null;
};

const parseModels = (data: ModelsResponse): ModelQuotaInfo[] => {
  if (!data.models) {
    return [];
  }

  return Object.entries(data.models).map(([modelName, modelData]) => ({
    modelName,
    displayName: modelData.displayName ?? modelName,
    remainingPercent: Math.round((modelData.quotaInfo?.remainingFraction ?? 1) * 100),
    resetTime: modelData.quotaInfo?.resetTime ?? null,
  }));
};

const findResetTime = (models: ModelQuotaInfo[]): number | null => {
  const target = models.find((model) => model.resetTime);
  if (!target?.resetTime) {
    return null;
  }

  return new Date(target.resetTime).getTime();
};

export const fetchAntigravityQuota = async (): Promise<QuotaResult> => {
  const accounts = await getAntigravityAccounts();

  if (!accounts?.accounts?.length) {
    return {
      provider: 'antigravity',
      ok: false,
      configured: false,
      summary: 'Not configured - no accounts found',
      data: null,
    };
  }

  const account = accounts.accounts[0];
  const accessToken = await refreshAccessToken(account.refreshToken);

  if (!accessToken) {
    return {
      provider: 'antigravity',
      ok: false,
      configured: true,
      summary: 'Failed to refresh OAuth token',
      data: null,
    };
  }

  const projectId = account.projectId ?? DEFAULT_PROJECT_ID;
  const modelsData = await fetchModels(accessToken, projectId);

  if (!modelsData) {
    return {
      provider: 'antigravity',
      ok: false,
      configured: true,
      summary: 'Failed to fetch models from API',
      data: null,
    };
  }

  const models = parseModels(modelsData);
  const claudeModels = models.filter(
    (model) =>
      model.modelName.toLowerCase().includes('claude') ||
      model.modelName.toLowerCase().includes('anthropic')
  );
  const geminiModels = models.filter((model) => model.modelName.toLowerCase().includes('gemini'));

  const data: AntigravityQuotaData = {
    email: account.email,
    models,
    claudeQuotaPercent: claudeModels.length
      ? Math.min(...claudeModels.map((model) => model.remainingPercent))
      : null,
    geminiQuotaPercent: geminiModels.length
      ? Math.min(...geminiModels.map((model) => model.remainingPercent))
      : null,
    claudeResetTime: findResetTime(claudeModels),
    geminiResetTime: findResetTime(geminiModels),
  };

  const summaryParts: string[] = [];
  if (data.claudeQuotaPercent !== null) {
    summaryParts.push(`Claude: ${data.claudeQuotaPercent}%`);
  }

  if (data.geminiQuotaPercent !== null) {
    summaryParts.push(`Gemini: ${data.geminiQuotaPercent}%`);
  }

  return {
    provider: 'antigravity',
    ok: true,
    configured: true,
    summary: summaryParts.length ? summaryParts.join(' | ') : 'No model quotas available',
    data,
  };
};
